import * as React from 'react';
import { ProfileType } from '@ixicare/resources';
import { useStores } from '~store/utils';
import { useObserverAlertMode } from '~hooks/useObserverAlertMode';
import { useSiteConfiguration } from '~containers/ConfigurationForm/ConfigurationForm';

export interface DutyGroupOption {
  id: string;
  label: string;
}

/**
 * The top bar's two axes, resolved for whoever is logged in.
 *
 * *Viewing* is which site is on screen; *duty* is whether you're handling
 * alarms, for which group. They're independent — browsing never changes duty —
 * and the bar is built so nothing appears on both sides.
 *
 * The off switch only exists for observers ({@link useObserverAlertMode}). An
 * HCP is on duty at their own site by definition, so for them duty is a fixed
 * fact and the control is only ever about group and availability. Anyone else
 * (an installer outside an organization, say) has no duty at all and gets a
 * plain presence pill.
 */
export function useDutyState() {
  const { alerts, auth, permissions, sites } = useStores();
  const observer = useObserverAlertMode();
  const { data: siteConfig } = useSiteConfiguration();

  // The multi-site gate, backend-side too: without it /sites returns only the
  // profile's own site (sites_index_endpoint), so there is nowhere else to be.
  const isMultiSite = permissions.has('can_view_site');

  const { isObserver, isDutyResolved, onDutySiteUuid, onDutySiteName, onDutyGroupName, selectedSiteUuid } = observer;
  const isHcp = auth.userType === ProfileType.HealthcareProvider;
  // An installer without org scope is a site admin, not an observer. They get
  // the HCP's duty axis so the group they receive for stays visible and
  // switchable; org admins are untouched and keep observer mode.
  const isSingleSiteInstaller = auth.isInstaller && !auth.user?.permissionOrganizationId;
  const isHcpLike = isHcp || isSingleSiteInstaller;

  const dutySiteUuid = isObserver ? onDutySiteUuid : isHcpLike ? auth.user?.site?.uuid : undefined;
  const dutySiteName = React.useMemo(() => {
    if (!dutySiteUuid) return undefined;
    // The sites list carries the canonical name; the profile's embedded site is
    // the fallback for users whose list hasn't loaded (or is a single entry).
    return (
      sites.sites.find((s) => s.uuid === dutySiteUuid)?.name ?? (isObserver ? onDutySiteName : auth.user?.site?.name)
    );
  }, [dutySiteUuid, sites.sites, isObserver, onDutySiteName, auth.user?.site?.name]);

  const viewedSiteName = React.useMemo(
    () => sites.sites.find((s) => s.uuid === selectedSiteUuid)?.name,
    [sites.sites, selectedSiteUuid]
  );

  const isOnDuty = !!dutySiteUuid;
  const isViewingDutySite = isOnDuty && dutySiteUuid === selectedSiteUuid;

  // Anyone who has duty but is looking somewhere else can only read here. That
  // covers the observer browsing another site *and* the multi-site HCP, who is
  // equally read-only away from their own site (see AlertActions).
  // Held back until the viewed site is known, so the tag can't flash during the
  // gap before the sites list loads.
  const showReadOnlyTag = !!selectedSiteUuid && (isObserver || isHcpLike) && !isViewingDutySite;

  // Only an observer may leave; an HCP always belongs to a group.
  const canStopDuty = isObserver;

  // Group is changeable while you're actually handling the site whose groups
  // these are. For HCPs that stays gated on the site's self-assign setting;
  // a site admin isn't bound by it.
  const isSelfAssignEnabled = siteConfig?.['enable-hcp-self-assign-hcp-group'] ?? false;
  const canChangeGroup = isViewingDutySite && (isObserver || isSingleSiteInstaller || isSelfAssignEnabled);

  const groupOptions: DutyGroupOption[] = React.useMemo(
    () => observer.groups.map((g) => ({ id: g.id, label: g.hcpGroupName })),
    [observer.groups]
  );

  return {
    isObserver,
    isHcp,
    isMultiSite,
    isDutyResolved,
    /** Writing presence or group membership while impersonating would change
     *  the *real* user's alarm routing from a session they can't see. The
     *  control stays visible — knowing their duty state is the point of
     *  looking — but read-only. */
    isReadOnly: auth.isImpersonating,
    /** No duty axis at all — render presence on its own. */
    hasDutyAxis: isObserver || isHcpLike,
    isOnDuty,
    isViewingDutySite,
    showReadOnlyTag,
    canStopDuty,
    canChangeGroup,
    /** The site setting alone — true even while you're looking elsewhere. */
    canSelfAssignGroup: isSelfAssignEnabled,

    dutySiteUuid,
    dutySiteName,
    dutyGroupId: auth.user?.hcpGroupId ?? null,
    dutyGroupName: onDutyGroupName,
    /** Alarms waiting where you're on duty — not where you're looking. */
    openCount: alerts.openCountFor(dutySiteUuid),

    viewedSiteUuid: selectedSiteUuid,
    viewedSiteName,

    groupOptions,
    isUnsegmentedSite: observer.isUnsegmentedSite,
    activate: observer.activate,
    deactivate: observer.deactivate,
  };
}
